import { html, TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { Property, PropertyDataUpdateLatch } from '../../../api/get-calendar-data-for-house';
import { formatDate } from '../../../helpers/date';
import { staticText } from '../../../static-text';
import { BookingData } from '../../../types/booking-data';
import { Dispatcher } from '../../../types/booking-request';
import { calendarControls } from './calendar-controls';
import { calendarDay } from './calendar-day';

let focusDate = new Date();

export function calendar(
	bookingData: BookingData,
	property: Property | null,
	propertyDataUpdateLatch: PropertyDataUpdateLatch,
	dispatch: Dispatcher
): TemplateResult {
	if ( !bookingData.houseId ) {
		throw new Error( 'houseId shouldn\'t be null here.' );
	}

	let endOfStay: Date | null = null;
	if ( bookingData.startOfStay ) {
		endOfStay = new Date( bookingData.startOfStay );
		endOfStay.setDate( endOfStay.getUTCDate() + bookingData.numberOfNights );
	}

	if ( !propertyDataUpdateLatch.loading ) {
		focusDate = bookingData.focusDate;
	}

	const firstDayOfCurrentCalendar = new Date( Date.UTC( focusDate.getUTCFullYear(), focusDate.getUTCMonth(), 1 ) );
	const lastDayOfMonth = new Date( Date.UTC( focusDate.getUTCFullYear(), focusDate.getUTCMonth() + 1, 0 ) );
	const dayOfWeekFirstDay = firstDayOfCurrentCalendar.getUTCDay() - 1; // '-1' because we want the week to start on monday.
	const dayOfWeekLastDay = lastDayOfMonth.getUTCDay() - 1; // '-1' because we want the week to start on monday.
	const daysInMonth = new Date( lastDayOfMonth ).getUTCDate() + dayOfWeekFirstDay + ( 6 - dayOfWeekLastDay );

	firstDayOfCurrentCalendar.setUTCDate( firstDayOfCurrentCalendar.getUTCDate() - dayOfWeekFirstDay );

	const d = new Date( firstDayOfCurrentCalendar );
	const days: Array<TemplateResult> = [];
	if ( property ) {
		for ( let i = 0; daysInMonth > i; i++ ) {
			const saturdayOfCurrentWeek = new Date( d );
			if ( 6 !== saturdayOfCurrentWeek.getUTCDay() ) {
				saturdayOfCurrentWeek.setUTCDate( saturdayOfCurrentWeek.getUTCDate() - ( ( saturdayOfCurrentWeek.getUTCDay() + 1 ) % 7 ) );
			}

			const weekData = property.thisMonth.find( ( week ) => {
				return (
					week.year === saturdayOfCurrentWeek.getUTCFullYear() &&
					week.month === saturdayOfCurrentWeek.getUTCMonth() + 1 &&
					( week.date ?? week.day_of_month ) === saturdayOfCurrentWeek.getUTCDate()
				);
			} );

			// This shouldn't be possible, but find could return undefined.
			if ( !weekData ) {
				throw new Error( `Expected weekData for ${formatDate( saturdayOfCurrentWeek )} but the corresponding WeekData could not be found inside calendarData returned from the API.` );
			}

			// Add calendar day to the days grid.
			const currentDate = new Date( d );
			days.push( calendarDay( bookingData.startOfStay, endOfStay, bookingData.houseId, dispatch, currentDate, saturdayOfCurrentWeek, weekData ) );

			// Update Current Date.
			d.setUTCDate( d.getUTCDate() + 1 );
		}
	}

	const classes = {
		'calendar--is-loading': propertyDataUpdateLatch.loading,
	};

	let loading = html``;
	if ( propertyDataUpdateLatch.loading ) {
		loading = html`
			<h3
				aria-live="polite"
				class="calendar__loading"
			>
				${staticText.loadingCalendarData}
			</h3>
		`;
	}

	return html`
		<div
			aria-hidden="true"
			class="calendar ${classMap( classes )}"
			tabindex="-1"
		>
			<div class="calendar__controls">
				${calendarControls( bookingData.houseId, focusDate, dispatch, propertyDataUpdateLatch )}
			</div>

			<div class="calendar__grid">
				<div class="calendar__day-label">M</div>
				<div class="calendar__day-label">T</div>
				<div class="calendar__day-label">W</div>
				<div class="calendar__day-label">T</div>
				<div class="calendar__day-label">F</div>
				<div class="calendar__day-label">S</div>
				<div class="calendar__day-label">S</div>
				${days}
			</div>

			${loading}
		</div>
	`;
}
